import { Howl, Howler } from 'howler';
import sfxSpriteData from '../static/audio/sfx-sprite.json';

// Background tracks that loop indefinitely
const LOOP_TRACKS = new Set(['game-bg-sound', 'game-level-music']);

// Background tracks — kept as individual Howls (long/loopable, referenced by audioSrc in level JSONs)
const TRACK_SOURCES = {
  'game-bg-sound':        [require('../static/audio/game-bg-sound.webm'),        require('../static/audio/game-bg-sound.mp3')],
  'game-level-music':     [require('../static/audio/game-level-music.webm'),     require('../static/audio/game-level-music.mp3')],
  'game-level-completed': [require('../static/audio/game-level-completed.webm'), require('../static/audio/game-level-completed.mp3')],
  'game-finished':        [require('../static/audio/game-finished.webm'),        require('../static/audio/game-finished.mp3')],
};

function makeHowl(name) {
  const src = TRACK_SOURCES[name];
  if (!src) return null;
  const howl = new Howl({
    src,
    loop: LOOP_TRACKS.has(name),
    preload: true,
    volume: 0.3,
    onplayerror: () => {
      howl.once('unlock', () => howl.play());
    },
  });
  return howl;
}

// Single Howl for all SFX (sprite mode — one network fetch, zero setup overhead per clip)
const _sfxHowl = new Howl({
  src: [
    require('../static/audio/sfx-sprite.webm'),
    require('../static/audio/sfx-sprite.mp3'),
  ],
  sprite: sfxSpriteData.sprite,
  preload: true,
  volume: 0.3,
  onplayerror: function () {
    _sfxHowl.once('unlock', () => { /* ready for next play */ });
  },
});
const _sfxNames = new Set(Object.keys(sfxSpriteData.sprite));

// Singleton state
let _muted = true;            // matches audioMuted default in context
let _currentTrackName = null;
let _currentSoundId = null;
const _howls = {};

// Pre-load all background tracks. Initialise Howler global mute to match default context state.
for (const name of Object.keys(TRACK_SOURCES)) {
  _howls[name] = makeHowl(name);
}
Howler.mute(true);

const SoundManager = {
  /**
   * Play a background track (or one-shot transition sound).
   * Stops the currently playing track first.
   */
  playTrack(name) {
    if (!_howls[name]) return;

    // Skip if already playing this exact track
    if (name === _currentTrackName && _howls[name].playing(_currentSoundId)) return;

    // Stop previous track
    if (_currentTrackName && _howls[_currentTrackName]) {
      _howls[_currentTrackName].stop();
    }

    _currentTrackName = name;
    if (!_muted) {
      _currentSoundId = _howls[name].play();
    }
  },

  /**
   * Play a one-shot SFX without stopping the current background track.
   */
  playSfx(name) {
    if (_muted || !_sfxNames.has(name)) return;
    _sfxHowl.play(name);
  },

  /**
   * Set global muted state.
   */
  setMuted(muted) {
    _muted = muted;
    Howler.mute(muted);
    // If unmuting and a track was queued, resume it
    if (!muted && _currentTrackName && _howls[_currentTrackName]) {
      if (!_howls[_currentTrackName].playing()) {
        _currentSoundId = _howls[_currentTrackName].play();
      }
    }
  },

  /**
   * Stop all sounds immediately.
   */
  stopAll() {
    Howler.stop();
    _currentTrackName = null;
    _currentSoundId = null;
  },
};

export default SoundManager;
